import { useParams, useNavigate, Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { ComplementsAPI, OrdersAPI } from '../api';
import { ChevronLeft, ChevronRight, X, Home } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { QuantitySelectionModal } from './QuantitySelectionModal';
import { SafeHtml } from './SafeHtml';

const ComplementDetail = () => {
    const { name } = useParams<{ name: string }>();
    const navigate = useNavigate();
    const { isSignedIn, user } = useUser();
    const clerk = useClerk();

    const [complement, setComplement] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [prevComplement, setPrevComplement] = useState<any>(null);
    const [nextComplement, setNextComplement] = useState<any>(null);
    const [relatedComplements, setRelatedComplements] = useState<any[]>([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    
    // Order logic state
    const [selectingId, setSelectingId] = useState<boolean>(false);
    const [quantityModalOpen, setQuantityModalOpen] = useState(false);
    const [selectedItemForQuantity, setSelectedItemForQuantity] = useState<any>(null);

    useEffect(() => {
        const checkScroll = () => {
            if (scrollContainerRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
                setCanScrollLeft(scrollLeft > 0);
                setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
            }
        };

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            setTimeout(checkScroll, 100);
            window.addEventListener('resize', checkScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScroll);
            }
            window.removeEventListener('resize', checkScroll);
        };
    }, [relatedComplements]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollAmount = direction === 'left' ? -300 : 300;
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const handleNextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!complement || !complement.photos || !selectedImage) return;
        const currentIndex = complement.photos.indexOf(selectedImage);
        if (currentIndex === -1) return;
        const nextIndex = (currentIndex + 1) % complement.photos.length;
        setSelectedImage(complement.photos[nextIndex]);
    };

    const handlePrevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!complement || !complement.photos || !selectedImage) return;
        const currentIndex = complement.photos.indexOf(selectedImage);
        if (currentIndex === -1) return;
        const prevIndex = (currentIndex - 1 + complement.photos.length) % complement.photos.length;
        setSelectedImage(complement.photos[prevIndex]);
    };

    const createOrder = async (item: any, quantity: number = 1) => {
        if (!user) return;

        setSelectingId(true);
        setQuantityModalOpen(false);

        try {
            const res = await OrdersAPI.create(
                user.id,
                item.id,
                {
                    userEmail: user.primaryEmailAddress?.emailAddress || '',
                    userName: user.fullName || user.firstName || 'Usuario',
                    planName: quantity > 1 ? `${item.displayName} (x${quantity})` : item.displayName,
                    planPrice: (item.price * quantity).toString(),
                    planCurrency: item.currency,
                    planPeriod: item.payTypeDisplay || (item.payType === 'ANUAL' ? 'Anual' : 'Mensual')
                },
                undefined,
                'complement',
                quantity
            );
            if (res.success) {
                navigate('/dashboard');
            } else {
                console.error('Error selecting plan:', res.error);
                alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.');
            }
        } catch (error) {
            console.error('Error selecting plan:', error);
            alert('Hubo un error al seleccionar el plan. Por favor intente nuevamente.');
        } finally {
            setSelectingId(false);
            setSelectedItemForQuantity(null);
        }
    };

    const handleSelectPlan = async (item: any) => {
        if (!isSignedIn) {
            clerk.openSignIn();
            return;
        }
        if (!user) return;

        if (item.displayName && item.displayName.toLowerCase().includes('zoho')) {
            setSelectedItemForQuantity(item);
            setQuantityModalOpen(true);
            return;
        }

        await createOrder(item);
    };

    useEffect(() => {
        const fetchComplement = async () => {
            if (!name) return;
            try {
                setLoading(true);
                setError(null);
                const decodedName = decodeURIComponent(name);
                const res = await ComplementsAPI.list();
                if (res.success && res.data) {
                    const found = res.data.find((c: any) => c.name === decodedName);
                    if (found) {
                        setComplement(found);

                        // Find prev/next complement
                        const sortedComplements = res.data
                            .filter((c: any) => c.isVisible !== false)
                            .sort((a: any, b: any) => (a.price || 0) - (b.price || 0));

                        const currentIndex = sortedComplements.findIndex((c: any) => c.id === found.id);
                        
                        if (currentIndex !== -1) {
                            const prev = sortedComplements[currentIndex - 1] || sortedComplements[sortedComplements.length - 1];
                            const next = sortedComplements[currentIndex + 1] || sortedComplements[0];
                            setPrevComplement(prev.id !== found.id ? prev : null);
                            setNextComplement(next.id !== found.id ? next : null);
                        }

                        // Find related complements (random selection excluding current)
                        const others = res.data.filter((c: any) => c.id !== found.id && c.isVisible !== false);
                        const shuffled = others.sort(() => 0.5 - Math.random());
                        setRelatedComplements(shuffled.slice(0, 4));
                    } else {
                        setError('Complemento no encontrado');
                    }
                } else {
                    setError('Error al cargar datos');
                }
            } catch (err) {
                console.error(err);
                setError('Error al cargar datos');
            } finally {
                setLoading(false);
            }
        };
        fetchComplement();
    }, [name]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando...</p>
            </div>
        );
    }

    if (error || !complement) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <button
                    onClick={() => navigate(-1)}
                    className="mb-8 flex items-center gap-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium"
                >
                    <ChevronLeft size={20} />
                    Volver
                </button>
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center">
                    <p className="text-red-600 dark:text-red-400 text-lg">{error || 'Complemento no encontrado'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 relative overflow-x-hidden">
            {/* Fixed Navigation Arrows */}
            {prevComplement && (
                <div
                    className="fixed left-0 top-1/2 -translate-y-1/2 z-40 group cursor-pointer hidden lg:block"
                    onClick={() => {
                        navigate(`/complemento/${encodeURIComponent(prevComplement.name)}`);
                        window.scrollTo(0, 0);
                    }}
                >
                    <div className="relative flex items-center">
                        {/* Hover Card (Expands from left) */}
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 shadow-xl border border-gray-100 dark:border-slate-700 rounded-r-xl h-24 w-12 group-hover:w-72 transition-all duration-300 overflow-hidden flex items-center z-10">
                            {/* Arrow Container (Always visible part) */}
                            <div className="absolute left-0 w-12 h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700/50 group-hover:bg-white dark:group-hover:bg-slate-800 transition-colors z-20">
                                <ChevronLeft size={24} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                            </div>

                            {/* Content (Hidden until hover) */}
                            <div className="pl-16 pr-4 flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-full min-w-[280px]">
                                {prevComplement.mainPhotoUrl ? (
                                    <img src={prevComplement.mainPhotoUrl} alt="" className="w-12 h-12 object-contain" />
                                ) : (
                                    <div className="w-12 h-12 bg-blue-50 dark:bg-slate-700 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold">
                                        {prevComplement.displayName?.substring(0, 2)}
                                    </div>
                                )}
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Anterior</span>
                                    <span className="font-bold text-sm text-gray-900 dark:text-white truncate block">{prevComplement.displayName}</span>
                                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{prevComplement.price} {prevComplement.currency}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {nextComplement && (
                <div
                    className="fixed right-0 top-1/2 -translate-y-1/2 z-40 group cursor-pointer hidden lg:block"
                    onClick={() => {
                        navigate(`/complemento/${encodeURIComponent(nextComplement.name)}`);
                        window.scrollTo(0, 0);
                    }}
                >
                    <div className="relative flex items-center justify-end">
                        {/* Hover Card (Expands from right) */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 shadow-xl border border-gray-100 dark:border-slate-700 rounded-l-xl h-24 w-12 group-hover:w-72 transition-all duration-300 overflow-hidden flex items-center z-10 justify-end">
                            
                            {/* Content (Hidden until hover) */}
                            <div className="pr-16 pl-4 flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-full min-w-[280px] justify-end text-right">
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Siguiente</span>
                                    <span className="font-bold text-sm text-gray-900 dark:text-white truncate block">{nextComplement.displayName}</span>
                                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{nextComplement.price} {nextComplement.currency}</span>
                                </div>
                                {nextComplement.mainPhotoUrl ? (
                                    <img src={nextComplement.mainPhotoUrl} alt="" className="w-12 h-12 object-contain" />
                                ) : (
                                    <div className="w-12 h-12 bg-blue-50 dark:bg-slate-700 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold">
                                        {nextComplement.displayName?.substring(0, 2)}
                                    </div>
                                )}
                            </div>

                            {/* Arrow Container (Always visible part) */}
                            <div className="absolute right-0 w-12 h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700/50 group-hover:bg-white dark:group-hover:bg-slate-800 transition-colors z-20">
                                <ChevronRight size={24} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
                <nav className="flex mt-8 mb-8" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-3">
                        <li className="inline-flex items-center">
                            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white">
                                <Home className="w-4 h-4 mr-2" />
                                Inicio
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                <Link to="/complementos" className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white">Complementos</Link>
                            </div>
                        </li>
                        <li aria-current="page">
                            <div className="flex items-center">
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">{complement.displayName}</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        {/* Left: Image / Gallery */}
                        <div className="p-8 lg:p-12 bg-gray-50 dark:bg-slate-900/50 flex flex-col items-center justify-center border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-slate-800">
                            {complement.mainPhotoUrl ? (
                                <img
                                    src={complement.mainPhotoUrl}
                                    alt={complement.displayName}
                                    className="w-full max-w-md object-contain max-h-[400px]"
                                />
                            ) : (
                                <div className="w-64 h-64 bg-gray-200 dark:bg-slate-800 rounded-full flex items-center justify-center text-4xl font-bold text-gray-400">
                                    {complement.displayName?.substring(0, 2).toUpperCase()}
                                </div>
                            )}

                            {/* Gallery Thumbnails */}
                            {complement.photos && complement.photos.length > 0 && (
                                <div className="mt-8 grid grid-cols-4 gap-4 w-full max-w-md">
                                    {complement.photos.map((photo: string, idx: number) => (
                                        <div
                                            key={idx}
                                            className="aspect-square rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
                                            onClick={() => setSelectedImage(photo)}
                                        >
                                            <img src={photo} alt="" className="w-full h-full object-cover" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right: Info */}
                        <div className="p-8 lg:p-12 space-y-8">
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                                    {complement.displayName}
                                </h1>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                                        {complement.currency} {complement.price}
                                    </span>
                                    <span className="text-lg text-gray-500 dark:text-gray-400">
                                        {complement.payTypeDisplay || (complement.payType === 'ANUAL' ? 'por año' : complement.payType === 'MENSUAL' ? 'por mes' : '')}
                                    </span>
                                </div>
                            </div>

                            <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-300">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Descripción</h3>
                                <SafeHtml
                                    html={complement.description || 'Sin descripción disponible.'}
                                    mode="rich"
                                />
                            </div>

                            <div className="pt-6 border-t border-gray-100 dark:border-slate-800">
                                {complement.paypalButtonHtml ? (
                                    <button 
                                        onClick={() => handleSelectPlan(complement)}
                                        disabled={selectingId}
                                        className="px-10 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {selectingId ? 'Procesando...' : 'Elegir plan'}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => window.open('mailto:contacto@tusitio.com?subject=Consulta sobre ' + complement.displayName)}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-full shadow-lg transition-transform active:scale-95"
                                    >
                                        Consultar / Contratar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {relatedComplements.length > 0 && (
                <div className="bg-[#012654] dark:bg-slate-900 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-2xl font-bold text-white mb-8">
                            Otras opciones
                        </h2>
                        <div className="relative group/carousel">
                            <div
                                ref={scrollContainerRef}
                                className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide scroll-smooth justify-center"
                            >
                                {relatedComplements.map((related: any) => (
                                    <div
                                        key={related.id}
                                        onClick={() => {
                                            navigate(`/complemento/${encodeURIComponent(related.name)}`);
                                            window.scrollTo(0, 0);
                                        }}
                                        className="min-w-[280px] w-[280px] bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer snap-start flex-shrink-0 border border-gray-100 dark:border-slate-700 p-6 flex flex-col items-center justify-center gap-4 group/card"
                                    >
                                        <div className="w-16 h-16 flex items-center justify-center bg-gray-50 dark:bg-slate-700 rounded-full group-hover/card:scale-110 transition-transform duration-300">
                                            {related.mainPhotoUrl ? (
                                                <img
                                                    src={related.mainPhotoUrl}
                                                    alt={related.displayName}
                                                    className="w-10 h-10 object-contain"
                                                />
                                            ) : (
                                                <span className="text-xl font-bold text-gray-400">
                                                    {related.displayName?.substring(0, 2)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-center">
                                            <h3 className="font-bold text-gray-900 dark:text-white group-hover/card:text-blue-600 dark:group-hover/card:text-blue-400 transition-colors">
                                                {related.displayName}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                {related.payTypeDisplay || (related.payType === 'ANUAL' ? 'Anual' : 'Mensual')}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                        {/* Carousel Controls - Only show if content overflows */}
                        {(canScrollLeft || canScrollRight) && (
                            <>
                                <button
                                    onClick={() => scroll('left')}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-white dark:bg-slate-700 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-white hover:scale-110 transition-all opacity-0 group-hover/carousel:opacity-100 disabled:opacity-0"
                                    disabled={!canScrollLeft}
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <button
                                    onClick={() => scroll('right')}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-white dark:bg-slate-700 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-white hover:scale-110 transition-all opacity-0 group-hover/carousel:opacity-100 disabled:opacity-0"
                                    disabled={!canScrollRight}
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </>
                        )}
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-200"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
                        onClick={() => setSelectedImage(null)}
                    >
                        <X size={32} />
                    </button>

                    <button
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-50 p-2 bg-black/50 rounded-full"
                        onClick={handlePrevImage}
                    >
                        <ChevronLeft size={40} />
                    </button>

                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-50 p-2 bg-black/50 rounded-full"
                        onClick={handleNextImage}
                    >
                        <ChevronRight size={40} />
                    </button>

                    <img
                        src={selectedImage}
                        alt="Vista ampliada"
                        className="max-w-[90%] max-h-[90vh] object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            <QuantitySelectionModal
                isOpen={quantityModalOpen}
                onClose={() => {
                    setQuantityModalOpen(false);
                    setSelectedItemForQuantity(null);
                }}
                item={selectedItemForQuantity}
                onConfirm={(qty) => createOrder(selectedItemForQuantity, qty)}
            />
        </div>
    );
};

export default ComplementDetail;
